import { capitalize, isEmpty, isFunction } from "lodash";
import React, { useMemo } from "react";
import { AssetEsDto } from "types/assetdetails";
import { formatArrayField } from "utils/assetUtils";
import { CellRenderers } from "utils/tableUtils";
import { CopyContent } from "views/Findings/FindingDetails/FindingDetailsSourceInfo";

export const useAssetRepositoryFields = (assetDetails: AssetEsDto) => {
  const repositoryFields = useMemo(() => {
    const repositoryDetails = {
      ...assetDetails.repositoryProperties,
      name: assetDetails?.name,
    };

    const formatRepositorySubType = (repositorySubType: string): string => {
      if (!repositorySubType) return "";
      if (repositorySubType === "CONTAINER_REPOSITORY") {
        return "Artifact Repository";
      }
      return repositorySubType
        .split("_")
        .map((word) => capitalize(word))
        .join(" ");
    };

    const subSectionFields =
      REPOSITORY_SUB_SECTION_MAP[
        repositoryDetails?.repositorySubType as keyof typeof REPOSITORY_SUB_SECTION_MAP
      ];
    const repositorySubType = formatRepositorySubType(
      repositoryDetails?.repositorySubType?.[0],
    );
    const fields = [
      {
        label: "Repository Type",
        name: "repositorySubType",
        containerClassName: "flex-column",
        formatter: () =>
          !isEmpty(repositoryDetails.repositorySubType)
            ? repositorySubType
            : CellRenderers.VALUE_OR_NA(),
        valueClassName: "break-content",
      },
      {
        label: "Region",
        name: "region",
        containerClassName: "flex-column",
        formatter: () => formatArrayField(assetDetails.location),
      },
      {
        label: "Architecture",
        name: "architecture",
        containerClassName: "flex-column",
        formatter: () => formatArrayField(repositoryDetails.architecture),
      },
      {
        label: "ARN",
        name: "arn",
        containerClassName: "flex-column",
        formatter: () => formatArrayField(assetDetails.arn),
        extra: !isEmpty(assetDetails.arn) ? (
          <CopyContent content={assetDetails.arn.join(", ")} fieldLabel="ARN" />
        ) : null,
      },
      ...(repositoryDetails?.repositorySubType
        ? [
            {
              label: `${repositorySubType} Details`,
              name: "header1",
            },
            ...(isFunction(subSectionFields)
              ? subSectionFields(repositoryDetails)
              : []),
          ]
        : []),
    ];
    return fields.map((repositoryField) => ({
      ...repositoryField,
      disabledPin: true,
    }));
  }, [assetDetails]);
  return repositoryFields;
};

const getContainerRepositoryDetails = (repositoryDetails: any) => [
  {
    label: "Native Type",
    name: "nativeTypes",
    containerClassName: "flex-column",
    formatter: () => formatArrayField(repositoryDetails.nativeTypes),
  },
];

export const REPOSITORY_SUB_SECTION_MAP = {
  CONTAINER_REPOSITORY: getContainerRepositoryDetails,
};
